import { useCallback, useEffect, useState } from 'react';
import { isDictationRecordingPurpose, recoversAsCapture } from '@cerebro-diem/core';
import RecordingRecovery from '../../components/RecordingRecovery';
import { listRawRecordings, type RawRecording } from '../../services/rawRecordings';
import { useAuthStore } from '../../stores/authStore';

/** Abandoned fields retain their original audio; another form must never claim it as a new answer. */
export default function KeptDictationRecordings() {
  const ownerId = useAuthStore(state => state.user?.id);
  const [read, setRead] = useState<{ ownerId: string; recordings: RawRecording[]; error: string | null } | null>(null);
  const refresh = useCallback(async () => {
    if (!ownerId) return;
    try {
      const recordings = (await listRawRecordings(ownerId)).filter(recording => isDictationRecordingPurpose(recording.purpose));
      if (useAuthStore.getState().user?.id === ownerId) setRead({ ownerId, recordings, error: null });
    } catch {
      if (useAuthStore.getState().user?.id === ownerId) setRead({ ownerId, recordings: [], error: 'Kept recording storage could not be read. Existing audio remains quarantined and untouched.' });
    }
  }, [ownerId]);
  useEffect(() => {
    void refresh();
    const reload = () => void refresh();
    window.addEventListener('focus', reload);
    return () => window.removeEventListener('focus', reload);
  }, [refresh]);
  if (read?.ownerId !== ownerId || (!read?.error && !read?.recordings.length)) return null;
  return <section className="bg-surface rounded-2xl p-4 space-y-3" aria-label="Kept dictation recordings">
    <h2 className="text-lg font-semibold">Kept dictation recordings</h2>
    <p>These recordings belong to this account and remain in this browser. Download the original audio to recover it. Exact question answers can also be retried in their original question.</p>
    {read.error && <p role="alert">{read.error}</p>}
    {read.recordings.map(recording => <div key={recording.id} className="space-y-2">
      <p>{new Date(recording.createdAt).toLocaleString()} · {recoversAsCapture(recording.purpose) ? 'Field dictation' : 'Question answer dictation'}</p>
      <RecordingRecovery ownerId={recording.ownerId} blob={recording.blob} />
    </div>)}
    <button type="button" onClick={() => void refresh()} className="text-primary underline">Refresh kept recordings</button>
  </section>;
}
